import axios from "axios";
import moment from "moment";
import * as React from "react";
import { store } from "../../store";
import config from "../../apiConfig";
import styled from "styled-components";
import { Badge, DatePicker, Empty, Popover, Select, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import searchIcon from "../../assets/images/ico-search.svg";
import filterIcon from "../../assets/images/filter.svg";
import { filterObject } from "../../utils/filterObject";
import { CalendarOutlined, CaretDownFilled, CreditCardOutlined, LoadingOutlined } from "@ant-design/icons";
import { LightenDarkenColor } from "../../utils/helpers";
import { getRandomColor } from "../../utils/randomColor";
import { useParams } from "react-router-dom";
import HTMLReactParser from "html-react-parser";
import AttachmentIcon from "../../assets/images/attachments.svg";
import { SelectedChatHistoryMessageContext } from "./Overview";
import { toCustomDateTimeFormat } from "../../helpers/util";

const Container = styled.div`
  width: 300px;
  border-right: 1px solid rgba(168, 171, 189, 0.4);
  padding: 0px 20px 20px 15px;
  max-height: 100%;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background-color: darkgrey;
    outline: 1px solid slategrey;
  }

  &:nth-child(2) {
    margin-top: 20px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 37px;
  background: white;
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const Search = styled.input`
  width: 250px;
  padding: 0 12px 0 40px;
  height: 30px;
  border-radius: 20px;
  border: solid 2px #e8e9f1;
  background-color: #ffffff;
  outline: none;
  font-family: Muli;
  font-size: 13px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
`;

const SeachIconContainer = styled.div`
  position: absolute;
  left: 15px;
`;

const CardContainer = styled.div`
  margin-top: 55px;
`;

const UserCardContainer = styled.div`
  min-height: 70px;
  max-height: 100px;
  margin-top: 12px;
  cursor: pointer;
  border-bottom: 2px solid
  ${(props) =>
  (props.theme.isSelected
    ? props.theme.borderColor
    : "rgba(168, 171, 189, 0.4)")};
`;

const Statics = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background-color: ${(props) => props.theme.background + "40"};
  color: ${(props) => LightenDarkenColor(props.theme.background, 5)};
  border: 1px solid ${(props) => props.theme.background};
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
`;

const Name = styled.div`
  flex: 1;
  text-transform: capitalize;
  font-size: 13px;
  width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #a8abbd;
  font-family: Muli;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  word-break: break-all;
  margin-right: 16px;
`;

const Time = styled.div`
  font-family: Muli;
  font-size: 13px;
  font-weight: normal;
  font-stretch: normal;
  font-style: italic;
  line-height: normal;
  letter-spacing: normal;
  color: #a8abbd;
`;

const Message = styled.div`
  color: ${(props) => (props.theme.isSelected ? "#2f3245" : "#a8abbd")};
  margin: 12px 0;
  font-family: Muli;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;
  word-break: break-all;
`;

interface IUserCardProps {
  message: any;
  name: string;
  id: string;
  time: string;
}

const UserCard = (props: IUserCardProps) => {
  const { id, name, message, time } = props;
  const { theme } = useSelector((state: any) => state.ui);
  const context = React.useContext(SelectedChatHistoryMessageContext);
  const isSelected = context.selectedChat === id;
  const getMessage = (type, msg) => {
    if (type == "pdf" || type == "image") {
      return (
        <div>
          <span>
            <img
              style={{
                width: "14px",
                marginRight: "6px",
              }}
              src={AttachmentIcon}
              alt="Attachment"
            />
          </span>
          <span>
            {msg.desc
              ? msg.desc.substr(0, 70)
              : msg.fileName && msg.fileName.substr(0, 70)}
          </span>{" "}
        </div>
      );
    } else if (type == "text") {
      return HTMLReactParser(msg.text.substr(0, 70));
    } else if (type == "adaptiveCard") {
      return (
        <div>
          <span>
            <CreditCardOutlined
              style={{
                marginRight: "6px",
                fontSize: "18px",
                verticalAlign: "0px",
              }}
            />
          </span>
          <span>
            {msg.data && msg.data.title ? msg.data.title : "Card View"}
          </span>
        </div>
      );
    } else {
      return "Message";
    }
  };
  const chatLastMsg = getMessage(message.messageType, message.content);
  return (
    <UserCardContainer
      theme={{ isSelected, borderColor: theme.secondaryColor }}
      onClick={() => context.setSelectedChat(id)}
    >
      <Statics>
        <Icon theme={{ background: getRandomColor(id) }}>
          {name[0].toUpperCase()}
        </Icon>
        <Name title={name}>{name}</Name>
        <Time>{time}</Time>
      </Statics>
      <Message theme={{ isSelected }}>{chatLastMsg}</Message>
    </UserCardContainer>
  );
}

export default function Sidebar() {
  const { Option } = Select;
  const [chatData, setChatData] = React.useState([]);
  const [active, setActive] = React.useState(false);
  const [chats, setChats] = React.useState({});
  const [searchText, setSearchText] = React.useState("");
  const [criteria, setCriteria] = React.useState<string>();
  const { botHandle } = useParams<{ botHandle: string }>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const {startDate, endDate} = useSelector((state: any) => state.ui.feedbackFilter);
  const dispatch = useDispatch();
  let lastTouched;

  const getFeedbackChat = async (append = false) => {
    if(!append)
      setIsLoading(true);
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${store.getState().auth.info.accessToken}`
      }
    };
    const params = new URLSearchParams();
    params.append("limit", "10");
    if (lastTouched) params.append("lastTouched", lastTouched);
    if (botHandle) params.append("botHandle", botHandle);
    if (startDate) params.append("startTime", startDate);
    if (endDate) params.append("endTime", endDate);
    if (criteria && criteria !== "all") params.append("criteria", criteria);

    const url = `${config.baseUrl}${config.org}${config.v1}/chatList?${params.toString()}`;
    try {
      let response = await axios(url, options);
      const newChats = response.data.chats;
      setIsLoading(false);
      if (append)
        setChatData(prev => [...prev, ...newChats]);
      else {
        setChatData(newChats);
        containerRef.current.scrollTo(0, 0);
      }

      lastTouched = new Date(newChats[newChats.length - 1].lastTouched).toISOString();
      setHasMore(newChats.length === 10);
    } catch (error) { }
  };

  React.useEffect(() => {
    (async () => {
      await getFeedbackChat();
    })();
  }, [botHandle, startDate, endDate, criteria])

  React.useEffect(() => {
    const updatedChats = filterObject(chatData, (chat) => chat && chat.fromName.toLowerCase().includes(searchText.toLowerCase()));
    setChats(updatedChats);
  }, [chatData]);

  React.useEffect(() => {
    const handleScroll = async () => {
      if (!containerRef.current || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        await getFeedbackChat(true);
      }
    };

    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll);
    }

    return () => el && el.removeEventListener("scroll", handleScroll);
  }, [hasMore]);


  const handleSearch = (e) => {
    const { value } = e.target;
    const updatedChats = filterObject(chatData, (chat) =>
      chat.fromName.toLowerCase().includes(value.toLowerCase())
    );
    setChats(updatedChats);
    setSearchText(value);
  };

  const handleDateChange = (field, value) => {
    const newFilter = {
      startDate: field === "startDate" ? (value ? value.toISOString() : "") : startDate,
      endDate: field === "endDate" ? (value ? value.toISOString() : "") : endDate,
    };
  
    // Dispatch once
    dispatch({ type: "FEEDBACK_FILTER", data: newFilter });
  };

  const filterContent = (
    <div
      style={{
        width: "270px",
        background: "#fff",
        color: "#121212"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>Filter</div>
      </div>
      <hr style={{ margin: "6px 0" }} />
      <div style={{ display: "flex", flexDirection: "column", marginBottom: "8px" }}>
        Start Date
        <DatePicker
          showTime
          allowClear={false}
          value={startDate ? moment(startDate) : null}
          format="DD/MM/YYYY, HH:mm"
          style={{ border: "1px solid #E5E5E5", borderRadius: "7px" }}
          suffixIcon={<CalendarOutlined style={{ color: "#757575", display: "inline-flex", alignItems: "center" }} />}
          onChange={(value) => handleDateChange("startDate", value)}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        End Date
        <DatePicker
          showTime
          allowClear={false}
          value={endDate ? moment(endDate) : null}
          format="DD/MM/YYYY, HH:mm"
          style={{ border: "1px solid #E5E5E5", borderRadius: "7px" }}
          suffixIcon={<CalendarOutlined style={{ color: "#757575", display: "inline-flex", alignItems: "center" }} />}
          onChange={(value) => handleDateChange("endDate", value)}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
        Criteria
        <Select style={{ width: "120px" }} defaultValue="all"
          value={criteria}
          onChange={(value) => setCriteria(value)}
        >
          <Option value="all">All</Option>
          <Option value="thumbsUp">Up Votes</Option>
          <Option value="thumbsDown">Down Votes</Option>
        </Select>
      </div>
    </div>
  );

  return (
    <Container className="overview" ref={containerRef}>
      <div style={{ position: "fixed", background: "#fff" }}>
        <SearchContainer>
          <SeachIconContainer>
            <img src={searchIcon} alt="search icon" />
          </SeachIconContainer>
          <Search
            value={searchText}
            onChange={handleSearch}
            placeholder="Search"
          />
          <Popover
            content={filterContent}
            trigger="click"
            visible={active}
            onVisibleChange={(visible) => setActive(visible)}
          >
            <Badge count={(startDate ? 1 : 0) + (endDate ? 1 : 0) + ((criteria !== "all" && criteria) ? 1 : 0)}
              style={{ height: "15px", minWidth: "15px", padding: 0, fontSize: 10, lineHeight: "15px", background: "#46B275" }}>
              <img style={{
                cursor: "pointer", filter: active
                  ? "invert(52%) sepia(83%) saturate(2229%) hue-rotate(345deg) brightness(95%) contrast(97%)"
                  : "none"
              }}
                src={filterIcon}
                alt="filter icon"
                onClick={() => setActive(!active)}
              />
            </Badge>
          </Popover>
        </SearchContainer>
      </div>
      <CardContainer>
        {isLoading ?
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} />
          </div> :
          Object.keys(chats).length > 0 ?
            Object.keys(chats).map((id: any, index: number) => {
              const {
                message,
                fromName,
                chatId,
                lastTouched
              } = chats[id];

              if (Object.keys(chats).length === index + 1) {
                return (
                  <UserCard
                    key={index}
                    id={chatId}
                    message={message}
                    name={fromName}
                    time={toCustomDateTimeFormat(lastTouched)}
                  />
                );
              }
              return (
                <UserCard key={index} id={chatId} message={message} name={fromName} time={toCustomDateTimeFormat(lastTouched)} />
              );
            }) :
            <div style={{ height: "60vh", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px" }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        }
      </CardContainer>
    </Container>
  );
}